import { Component } from "react";
import { connect } from "react-redux";
import {
    Modal,
    Button,
    Card,
    Space,
    Typography,
    Progress,
    Spin,
    message
} from "antd";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    FileTextOutlined
} from "@ant-design/icons";
import { convertFromRaw, EditorState } from "draft-js";

import RichTextPreview from "./RichTextPreview";
import QuizQuestionOption from "../Collections/Video/QuizQuestions/QuizQuestionOption";
import { submitQuizQuestionAttempt } from "../actions/collection";

const { Text, Title } = Typography;

class ExamPopup extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currentQuestionIndex: 0,
            answers: {},
            submittedQuestions: {},
            showResults: false,
            submitting: false,
            results: {}
        };
    }

    componentDidMount() {
        if (this.props.examQuestions && this.props.examQuestions.length > 0) {
            const answers = {};
            this.props.examQuestions.forEach((q) => {
                answers[q.id] = [];
            });
            this.setState({ answers });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.examQuestions !== this.props.examQuestions &&
            this.props.examQuestions &&
            this.props.examQuestions.length > 0
        ) {
            const answers = {};
            this.props.examQuestions.forEach((q) => {
                answers[q.id] = [];
            });
            this.setState({
                answers,
                currentQuestionIndex: 0,
                submittedQuestions: {},
                showResults: false,
                results: {}
            });
        }
        const len = this.props.examQuestions?.length;
        if (len != null && len > 0) {
            const idx = this.state.currentQuestionIndex;
            const clamped = Math.min(Math.max(0, idx), len - 1);
            if (idx !== clamped) {
                this.setState({ currentQuestionIndex: clamped });
            }
        }
    }

    handleOptionClick = (questionId, optionId) => {
        const { examQuestions } = this.props;
        const question = examQuestions.find((q) => q.id === questionId);
        if (!question || this.state.submittedQuestions[questionId]) return;

        const currentAnswers = [
            ...(this.state.answers[questionId] || [])
        ];

        if (question.questionType === "select_one") {
            this.setState({
                answers: {
                    ...this.state.answers,
                    [questionId]: currentAnswers.includes(optionId)
                        ? []
                        : [optionId]
                }
            });
        } else if (question.questionType === "choose_all_that_apply") {
            const index = currentAnswers.indexOf(optionId);
            if (index > -1) {
                currentAnswers.splice(index, 1);
            } else {
                currentAnswers.push(optionId);
            }
            this.setState({
                answers: {
                    ...this.state.answers,
                    [questionId]: currentAnswers
                }
            });
        } else {
            this.setState({
                answers: {
                    ...this.state.answers,
                    [questionId]: [optionId]
                }
            });
        }
    };

    handleSubmitQuestion = (question) => {
        const { collection, module: examModule, submitQuizQuestionAttempt } = this.props;
        const selectedAnswers = this.state.answers[question.id] || [];

        if (selectedAnswers.length === 0) {
            message.warning("Please select an answer before submitting.");
            return;
        }

        if (this.state.submitting || this.state.submittedQuestions[question.id]) {
            return;
        }

        const finishSubmission = () => {
            this.setState((prevState) => ({
                submitting: false,
                submittedQuestions: {
                    ...prevState.submittedQuestions,
                    [question.id]: true
                }
            }));
        };

        // GPT-sourced exam questions (from "Generate MCQ") are not stored as QuizQuestion; skip attempt API
        if (question.source === "gpt") {
            this.setState({ submitting: true });
            finishSubmission();
            return;
        }

        const videoId = question.video;

        // Use exam module when taking a section exam; otherwise find module from collection
        const moduleId = examModule
            ? examModule.id
            : (collection &&
                  collection.modules.find((m) =>
                      m.videos.some((v) => v.id === videoId)
                  ))?.id ?? null;

        if (!moduleId || !videoId) {
            message.error(
                "Unable to determine module/video for this question."
            );
            return;
        }

        this.setState({ submitting: true });

        submitQuizQuestionAttempt(
            collection.id,
            moduleId,
            videoId,
            question.id,
            selectedAnswers,
            "",
            null,
            finishSubmission,
            () => this.setState({ submitting: false })
        );
    };

    handleNext = () => {
        if (
            this.state.currentQuestionIndex <
            this.props.examQuestions.length - 1
        ) {
            this.setState({
                currentQuestionIndex: this.state.currentQuestionIndex + 1
            });
        }
    };

    handlePrevious = () => {
        if (this.state.currentQuestionIndex > 0) {
            this.setState({
                currentQuestionIndex: this.state.currentQuestionIndex - 1
            });
        }
    };

    handleFinishExam = () => {
        this.setState({ showResults: true });
    };

    render() {
        const {
            visible,
            onClose,
            examQuestions,
            examQuestionsStatus,
            module
        } = this.props;

        const examTitle = module ? `Exam: ${module?.name ?? "Section"}` : "Exam";

        if (!visible) {
            return null;
        }

        if (examQuestionsStatus === "LOADING") {
            return (
                <Modal
                    visible={visible}
                    onCancel={onClose}
                    footer={null}
                    width={800}
                    title={`${examTitle} – Loading…`}>
                    <div style={{ textAlign: "center", padding: 40 }}>
                        <Spin size="large" />
                    </div>
                </Modal>
            );
        }

        if (!examQuestions || examQuestions.length === 0) {
            return (
                <Modal
                    visible={visible}
                    onCancel={onClose}
                    footer={null}
                    width={800}
                    title={examTitle}>
                    <Text>
                        There are no multiple-choice questions available for
                        {module ? " this section yet." : " this course yet."}
                    </Text>
                </Modal>
            );
        }

        const safeIndex = Math.min(
            Math.max(0, this.state.currentQuestionIndex),
            examQuestions.length - 1
        );
        const currentQuestion = examQuestions[safeIndex];
        const progress =
            ((safeIndex + 1) / examQuestions.length) * 100;
        const submittedCount = Object.keys(
            this.state.submittedQuestions
        ).length;

        if (this.state.showResults) {
            let correctCount = 0;
            let answeredCount = 0;
            examQuestions.forEach((q) => {
                const selected = (this.state.answers[q.id] || []).slice();
                if (selected.length === 0) return;
                answeredCount += 1;
                const correctIds = (q.options || [])
                    .filter((o) => o.correctAnswerFlag)
                    .map((o) => o.id);
                const selectedSet = new Set(selected.map(String));
                const correctSet = new Set(correctIds.map(String));
                if (
                    selectedSet.size === correctSet.size &&
                    [...selectedSet].every((id) => correctSet.has(id))
                ) {
                    correctCount += 1;
                }
            });
            const totalCount = examQuestions.length;
            const pct =
                totalCount > 0
                    ? Math.round((correctCount / totalCount) * 100)
                    : 0;
            return (
                <Modal
                    visible={visible}
                    onCancel={onClose}
                    footer={[
                        <Button key="close" onClick={onClose}>
                            Close
                        </Button>
                    ]}
                    width={800}
                    title={`${examTitle} – Complete`}>
                    <div style={{ textAlign: "center", padding: 20 }}>
                        <Title level={3}>
                            <CheckCircleOutlined /> Exam Completed
                        </Title>
                        <Text style={{ display: "block", marginBottom: 8 }}>
                            You got {correctCount} out of {totalCount} correct
                            {totalCount > 0 ? ` (${pct}%)` : ""}.
                        </Text>
                        <Text type="secondary">
                            You answered {answeredCount} of {totalCount}{" "}
                            questions.
                        </Text>
                    </div>
                </Modal>
            );
        }

        const emptyDraftRaw = {
            blocks: [
                {
                    key: "a",
                    text: "",
                    type: "unstyled",
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: []
                }
            ],
            entityMap: {}
        };
        const rawText =
            currentQuestion?.text &&
            typeof currentQuestion.text === "object" &&
            currentQuestion.text?.blocks?.length
                ? currentQuestion.text
                : emptyDraftRaw;
        const contentStateText = convertFromRaw(rawText);
        const editorStateText =
            EditorState.createWithContent(contentStateText);
        const selectedForCurrent =
            this.state.answers[currentQuestion.id] || [];

        return (
            <Modal
                visible={visible}
                onCancel={onClose}
                width={900}
                footer={[
                    <Button
                        key="prev"
                        onClick={this.handlePrevious}
                        disabled={safeIndex === 0}>
                        Previous
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={this.state.submitting}
                        onClick={() => this.handleSubmitQuestion(currentQuestion)}>
                        Submit
                    </Button>,
                    <Button
                        key="next"
                        onClick={this.handleNext}
                        disabled={safeIndex === examQuestions.length - 1}>
                        Next
                    </Button>,
                    <Button
                        key="finish"
                        type="primary"
                        icon={<FileTextOutlined />}
                        onClick={this.handleFinishExam}>
                        Finish Exam
                    </Button>
                ]}
                title={`${examTitle} – Question ${safeIndex + 1} of ${examQuestions.length}`}>
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Progress percent={progress} showInfo={false} />
                    <Text type="secondary">
                        Submitted {submittedCount}/{examQuestions.length}
                    </Text>
                    <Card>
                        <Space direction="vertical" style={{ width: "100%" }}>
                            <Title level={4}>
                                {currentQuestion?.title ?? "Question"}
                            </Title>
                            <RichTextPreview editorState={editorStateText} />
                            <Space
                                direction="vertical"
                                style={{ width: "100%", marginTop: 16 }}>
                                {(currentQuestion.options || []).map((option) => (
                                    <QuizQuestionOption
                                        key={option.id}
                                        handleOnClick={() =>
                                            this.handleOptionClick(
                                                currentQuestion.id,
                                                option.id
                                            )
                                        }
                                        selected={selectedForCurrent.includes(
                                            option.id
                                        )}
                                        latestAttempt="preview"
                                        submitted={
                                            !!this.state.submittedQuestions[
                                                currentQuestion.id
                                            ]
                                        }
                                        isOpenEndedQuestion={false}
                                        option={option}
                                    />
                                ))}
                            </Space>
                        </Space>
                    </Card>
                </Space>
            </Modal>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        examQuestions: state.collectionReducer.examQuestions,
        examQuestionsStatus: state.collectionReducer.examQuestionsStatus
    };
};

export default connect(mapStateToProps, {
    submitQuizQuestionAttempt
})(ExamPopup);

